import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Drop-in PayPal smart button. Used on /pricing.
 * Loads PayPal config from /api/paypal/config and renders the official PayPal Smart Buttons.
 * On approve → captures via backend → AI auto-fulfills the order.
 *
 * Props:
 *   product_id   – id of the SERVICE_PRICES entry (e.g. "hustle_plan")
 *   price        – display string (e.g. "£5")
 *   onSuccess    – callback(order) when fulfilment completes
 */
export default function PayPalCheckoutButton({ product_id, price, onSuccess }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/paypal/config");
        setConfig(data);
      } catch (err) {
        console.warn("paypal config failed", err);
        setConfig({ configured: false });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="h-12 animate-pulse rounded-md bg-secondary/40" data-testid="paypal-loading" />;
  }
  if (!config?.configured || !config?.client_id) {
    return (
      <div className="rounded-md border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-[11px] text-amber-400" data-testid="paypal-not-configured">
        PayPal not configured yet. Falling back to manual paypal.me — set PAYPAL_CLIENT_ID + PAYPAL_CLIENT_SECRET in backend env to enable instant checkout.
      </div>
    );
  }

  const opts = {
    clientId: config.client_id,
    currency: "GBP",
    intent: "capture",
    // sandbox mode renders test buttons; live = real money
    "data-sdk-integration-source": "zuntria",
    components: "buttons",
  };

  return (
    <div data-testid={`paypal-button-${product_id}`}>
      <PayPalScriptProvider key="paypal-capture" options={opts}>
        <PayPalButtons
          style={{ layout: "horizontal", shape: "rect", color: "blue", label: "paypal", height: 45 }}
          createOrder={async () => {
            try {
              const { data } = await api.post("/paypal/create-order", { product_id });
              return data.paypal_order_id;
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not start PayPal checkout");
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await api.post(`/paypal/capture/${data.orderID}`);
              if (res.data?.status === "fulfilled") {
                toast.success(`Payment received — your ${res.data.order?.label || "order"} is ready!`, {
                  description: `${price} captured. Check Documents or Dashboard.`,
                  duration: 10000,
                });
                onSuccess?.(res.data.order);
              } else {
                toast.info("Payment pending — we'll deliver as soon as PayPal confirms.");
              }
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Capture failed");
            }
          }}
          onError={(err) => {
            console.error("paypal button error", err);
            toast.error("PayPal had an issue. Try again or use Revolut.");
          }}
          onCancel={() => toast.info("Payment cancelled")}
        />
      </PayPalScriptProvider>
    </div>
  );
}
